import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import filteredObjectArraySelector from 'functions/filteredObjectArraySelector'

import * as interactiveImageActions from 'agency/redux/modules/interactiveImage'

import useWindowScale from './hooks/useWindowScale'
import InteractiveImageTag from './InteractiveImageTag'

import styles from './styles.scss'

const fetchInteractiveImage = (props) => {
  const {
    interactiveImageId,
    loadedInteractiveImageIds,
    loadingInteractiveImages,
    interactiveImageErrors,
    loadInteractiveImage,
  } = props

  if (
    interactiveImageId
    && !loadedInteractiveImageIds.includes(interactiveImageId)
    && !loadingInteractiveImages
    && !interactiveImageErrors.length
  ){
    const options = {
      include_image_sizes: true,
      include_interactive_image_sizes: true,
      include_interactive_image_tag_image: true,
      include_interactive_image_tags: true,
    }

    loadInteractiveImage(interactiveImageId, options)
  }
}

const callbacks = (component, props, setSelectedImage) => {
  const componentCallbacks = {
    InteractiveImageTag: {
      selectImage: image => setSelectedImage(image),
    },
  }

  return componentCallbacks[component] || {}
}

const renderImageTags = (props, scale, setSelectedImage) => {
  const { interactiveImageTags, images } = props

  return interactiveImageTags.map((interactiveImageTag) => {
    const image = images[interactiveImageTag.image_id] || {}

    return (
      <InteractiveImageTag
        key={interactiveImageTag.id}
        interactiveImageTag={interactiveImageTag}
        scale={scale}
        image={image}
        callbacks={callbacks('InteractiveImageTag', props, setSelectedImage)}
      />
    )
  })
}

const getPreviewSize = (image, scale) => {
  const { size } = image
  const { width, height } = (size && size.preview) || {}

  return {
    previewWidth: width * scale - 10,
    previewHeight: height * scale - 10,
  }
}

const InteractiveImage = (props) => {
  const {
    interactiveImageId,
    interactiveImage: { large_url, size },
    interactiveImageTags,
    images,
  } = props

  const [selectedImage, setSelectedImage] = useState({})
  const { width, height } = (size && size.preview) || {}
  const scale = useWindowScale(width)

  useEffect(() => fetchInteractiveImage(props), [interactiveImageId])

  const memoRenderImageTags = useMemo(() => renderImageTags(props, scale, setSelectedImage), [
    interactiveImageTags,
    images,
    scale,
  ])

  const canvasStyle = {
    width: width ? width * scale : null,
    height: height ? height * scale : null,
    backgroundImage: `url(${large_url})`,
  }

  const fullPreviewClass = selectedImage.id ? styles.fullPreview : styles.hidden
  const { previewWidth, previewHeight } = selectedImage.id ? getPreviewSize(selectedImage, scale) : {}

  return (
    <div className={styles.root}>
      <section className={styles.stage}>
        <div className={styles.canvas} style={canvasStyle}>
          {memoRenderImageTags}
          <div className={fullPreviewClass} onClick={() => setSelectedImage({})} style={{ width: previewWidth + 6 }}>
            <img src={selectedImage.large_url} style={{ width: previewWidth, height: previewHeight }} alt="preview" />
          </div>
        </div>
      </section>
    </div>
  )
}

InteractiveImage.propTypes = {
  images: PropTypes.object,
  interactiveImage: PropTypes.object,
  interactiveImageId: PropTypes.string.isRequired,
  interactiveImageTags: PropTypes.array,
}

function mapStateToProps(state, props){
  const {
    match: {
      params: { interactiveImageId },
    },
  } = props
  const { interactiveImages, images } = state.entities
  const interactiveImage = interactiveImages[interactiveImageId] || {}

  return {
    images,
    interactiveImage,
    interactiveImageErrors: state.interactiveImages.errors,
    interactiveImageId,
    loadedInteractiveImageIds: state.interactiveImages.loadedIds,
    loadingInteractiveImages: state.interactiveImages.loading,
    interactiveImageTags: filteredObjectArraySelector(
      state,
      'interactiveImageTags',
      'interactive_image_id',
      interactiveImage.id,
    ),
  }
}

const mapDispatchToProps = {
  loadInteractiveImage: interactiveImageActions.loadInteractiveImage,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(InteractiveImage)
